import { flow, SnapshotIn } from 'mobx-state-tree';
import Hades from '@services/Hades';
import {
  IOrganizationalGroups,
  IOrganizationalServices,
} from '@stores/ApplicationInterfaces';
import OrganizationalGroup from '@stores/OrganizationalGroup';
import OrganizationalService from '@stores/OrganizationalService';
import { DEFAULT_GROUP_ID } from '@utils/mst/organizationalHelpers';

export default {
  actions: (self: {
    hades: Hades;
    organizationalGroups: IOrganizationalGroups;
    organizationalServices: IOrganizationalServices;
  }) => ({
    createOrganizationalGroup: flow(function* createOrganizationalGroup({
      name,
      description,
      serviceIds,
    }: {
      name: string;
      description: string;
      serviceIds: number[];
    }) {
      const result: SnapshotIn<typeof OrganizationalGroup> =
        yield self.hades.request('organizationalGroup.create', {
          name,
          description,
          serviceIds,
        });

      if (result) {
        const processedResult = {
          ...result,
          services: result.services ? result.services.map((service: any) => service.id || service) : [],
        };
        self.organizationalGroups.set(`${processedResult.id}`, processedResult);
      }

      return result;
    }),

    updateOrganizationalGroup: flow(function* updateOrganizationalGroup({
      id,
      name,
      description,
      serviceIds,
    }: {
      id: number;
      name: string;
      description: string;
      serviceIds: number[];
    }) {
      const result: SnapshotIn<typeof OrganizationalGroup> =
        yield self.hades.request('organizationalGroup.update', {
          orgGroupId: id,
          name,
          description,
          serviceIds,
        });

      if (result) {
        const processedResult = {
          ...result,
          services: result.services ? result.services.map((service: any) => service.id || service) : [],
        };
        self.organizationalGroups.set(`${processedResult.id}`, processedResult);
      }

      return result;
    }),

    deleteOrganizationalGroup: flow(function* deleteOrganizationalGroup({
      id,
    }: {
      id: number;
    }) {
      if (id === DEFAULT_GROUP_ID) {
        throw new Error('Cannot delete the default group');
      }

      try {
        const response = yield self.hades.request('organizationalGroup.delete', { orgGroupId: id });

        if (!response) {
          throw new Error('Failed to delete organizational group: no response received');
        }

        if (self.organizationalGroups.has(`${id}`)) {
          self.organizationalGroups.delete(`${id}`);
        }
      } catch (error) {
        console.error('Error during delete:', error);
        throw error;
      }
    }),

    getOrganizationalGroups: flow(function* getOrganizationalGroups() {
      const result: SnapshotIn<typeof OrganizationalGroup>[] = yield self.hades.request('organizationalGroup.list');

      if (result) {
        result.forEach((group) => {
          const processedGroup = {
            ...group,
            services: group.services ? group.services.map((service: any) => service.id || service) : [],
          };
          self.organizationalGroups.set(`${processedGroup.id}`, processedGroup);
        });
      }

      return result;
    }),

    getOrganizationalServices: flow(function* getOrganizationalServices() {
      const result: SnapshotIn<typeof OrganizationalService>[] = yield self.hades.request('service.list');

      if (result) {
        result.forEach((service) => {
          self.organizationalServices.set(`${service.id}`, service);
        });
      }

      return result;
    }),
  }),
};
